import type { IRawStyle } from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";
import type { AppGalleryButtonProps } from "../AppGalleryButton";
import type { AppGalleryStyles } from "./AppGallery.styles";

/**
 * The functions in this file are needed because most galleries
 * do not pass a defaultWidth. VSO Task 4413890 tracks passing
 * default widths instead
 */
export const expandedGalleryChildrenGap = 2;

/**
 * Returns the width of a gallery based on the item width, number
 * of columns, and padding between elements
 * @param columnCount default number of columns
 * @param itemWidth width of individual items
 */
export const getGalleryDefaultWidth = memoizeFunction(
  (columnCount: number, itemWidth: number): number => {
    // To calculate the width, we need to ensure we account for the
    // children gap padding between elements and on the edges of the gallery
    const itemPadding = (columnCount + 1) * expandedGalleryChildrenGap;
    const result = columnCount * itemWidth + itemPadding;

    return result;
  }
);

// Returns the width set on the custom styles for galleryButtonInExpanded
export const getItemWidth = (
  customStyles?: Partial<AppGalleryStyles>
): number | undefined => {
  const rootStyles: IRawStyle = customStyles?.galleryButtonInExpanded
    ?.root as IRawStyle;
  const itemWidth = rootStyles && rootStyles.width;

  // If it is a number we can return it directly
  if (typeof itemWidth === "number") {
    return itemWidth;
  }
  // If it is a string, we need to parse the numbers out
  if (typeof itemWidth === "string") {
    return parseInt(itemWidth, 10);
  }

  return undefined;
};

/**
 * Returns the desired width of a gallery based on the input,
 * If we have customStyles we will use that first to get the width
 * of the item. If not, we will use the passed in item width to
 * calculate the default width based on the columns
 */
export const getGalleryWidth = (
  columnCount: number,
  itemWidth: number,
  galleryButtons?: AppGalleryButtonProps[],
  customStyles?: Partial<AppGalleryStyles>
): number | undefined => {
  const customStylesWidth = customStyles && getItemWidth(customStyles);
  const galleryItemWidth = customStylesWidth || itemWidth;
  const actualColumnCount = galleryButtons
    ? Math.min(galleryButtons.length, columnCount)
    : columnCount;

  return (
    galleryItemWidth &&
    getGalleryDefaultWidth(actualColumnCount, galleryItemWidth)
  );
};
